import { TenantCredentialId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import React, { useCallback, useMemo } from "react";
import { useCurrentRegion, useIsMultiRegionUser } from "store/selectors/hooks";
import { QuickBooksConnection } from "./quick-books-connection";

interface OtherQuickBooksConnectionsProps {
    currentCredentialId: TenantCredentialId | null;
    onChanged: () => void;
}

export const OtherQuickBooksConnections: React.FC<OtherQuickBooksConnectionsProps> = ({
    currentCredentialId,
    onChanged,
}) => {
    const currentRegion = useCurrentRegion();
    const isMultiRegion = useIsMultiRegionUser();
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.integrations.getQuickBooksCredentials.queryOptions({}),
    });

    const otherCredentials = useMemo(() => {
        if (!query.data) return [];
        return query.data.filter(
            c => c.id !== currentCredentialId && c.regionVisibility?.editable === true,
        );
    }, [query.data, currentCredentialId]);

    const handleChanged = useCallback(() => {
        query.refetch();
        onChanged();
    }, [query, onChanged]);

    if (!otherCredentials.length) return null;

    return (
        <div className="space-y-4 lg:space-y-8">
            {isMultiRegion && currentRegion ? (
                <InlineBanner type="info" fontStyle="medium">
                    The following connections are active in other offices in your agency.
                </InlineBanner>
            ) : null}
            {otherCredentials.map(credential => (
                <QuickBooksConnection
                    key={credential.id}
                    credential={credential}
                    onDeleted={handleChanged}
                    onSaved={handleChanged}
                />
            ))}
        </div>
    );
};
